import React, { useRef, useEffect, useContext, useCallback } from 'react'
import { AppContext, DispatchContext } from "../AppContext";


export default function CanvasCompare(props) {
    const { onHover, onClick, hoveredPIdx, ...canvasProps } = props;

    const state = useContext(AppContext);
    const s = state.strings;
    const dispatch = useContext(DispatchContext);
    const canvasRef = useRef(null);
    const positionsRef = useRef({});
    const layoutRef = useRef({});

    // Calculate positions and layout data
    const calculateLayout = useCallback(() => {
        if (!state.sourceMarkup)
            return null;

        var length1 = state.sourceMarkup.Text1.Length;
        var length2 = state.sourceMarkup.Text2.Length;
        var maxLength = Math.max(length1, length2);

        var width = 900;
        var textPadding = 5;
        var barPadding = 10;
        var scale = width / maxLength;
        // true  = Text1 on top,  Text2 below
        // false = Text2 on top,  Text1 below (original layout)
        var text1OnTop = true;
        var y1 = text1OnTop ? 10  : 80;
        var y2 = text1OnTop ? 80  : 10;
        var height = 25;
        var bezierOffset = 25;
        var gap = 2;

        // reformat text markup into phrases 
        var positions = {};
        state.sourceMarkup.Text1.Markup.filter((source) => (source.PIdx != null)).forEach((source) => {
            positions[source.PIdx] = {
                start1: source.Start * scale,
                end1: source.End * scale
            };
        });
        state.sourceMarkup.Text2.Markup.filter((source) => (source.PIdx != null)).forEach((source) => {
            positions[source.PIdx] = {
                ...positions[source.PIdx],
                start2: source.Start * scale,
                end2: source.End * scale
            };
        });

        // shift upper or lower bar to bring corresponding positions close together
        var shift = 0;
        Object.keys(positions).forEach((key) => {
            var pos = positions[key];
            shift += (pos.start2 - pos.start1);
        });
        shift /= Object.keys(positions).length || 1;
        
        var shift1 = barPadding + ((shift > 0) ? shift : 0);
        var shift2 = barPadding + ((shift < 0) ? -shift : 0);
        shift1 = Math.min(shift1, width + barPadding - (length1 * scale));
        shift2 = Math.min(shift2, width + barPadding - (length2 * scale));

        return {
            positions,
            layout: {
                length1, length2, width, textPadding, barPadding, scale,
                y1, y2, height, bezierOffset, gap, shift1, shift2
            }
        };
    }, [state.sourceMarkup]);

    // Draw the canvas
    const draw = useCallback((highlightedKey = null) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const data = calculateLayout();
        if (!data) return;

        const { positions, layout } = data;
        const { length1, length2, width, textPadding, y1, y2, height, bezierOffset, gap, shift1, shift2, scale } = layout;
        
        positionsRef.current = positions;
        layoutRef.current = layout;

        var emptyColor = "#CCCCCC";   // mid gray — text without any sources
        var borderColor = "#BBBBBB";
        var textColor = "#111111";
        var excludedColor = "#F0F0F0"; // very light gray — excluded regions

        var ctx = canvas.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
        ctx.globalAlpha = 1.0; // Reset alpha
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.translate(0.5, 0.5);

        // draw background
        ctx.fillStyle = emptyColor;
        ctx.shadowColor = 'gray';
        ctx.shadowBlur = 10;
        ctx.fillRect(shift1, y1, length1 * scale, height);
        ctx.fillRect(shift2, y2, length2 * scale, height);

        // draw excluded regions on Text1 (lower bar) as steel blue
        // ExcludeRegions uses byte offsets identical to the plag Start/End coordinate system
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = excludedColor;
        const excludeRegions = state.sourceMarkup.ExcludeRegions || [];
        excludeRegions.forEach((region) => {
            const exX = shift1 + region.start * scale;
            const exW = Math.max(1.0, (region.end - region.start) * scale);
            ctx.fillRect(exX, y1, exW, height);
        });

        // draw markup rects and bezier lines between them
        ctx.shadowBlur = 1;
        Object.keys(positions).forEach((key) => {
            var pos = positions[key];
            var baseColor = state.ui.colors[key % 10];
            var isHighlighted = highlightedKey !== null && String(key) === String(highlightedKey);
            
            ctx.globalAlpha = 1.0;
            ctx.fillStyle = baseColor;
            ctx.strokeStyle = baseColor;

            var width1 = Math.max(1.0, pos.end1 - pos.start1);
            ctx.fillRect(shift1 + pos.start1, y1, width1, height);
            var width2 = Math.max(1.0, pos.end2 - pos.start2);
            ctx.fillRect(shift2 + pos.start2, y2, width2, height);

            // Ribbon runs from bottom of top bar (y1+height) to top of bottom bar (y2)
            ctx.beginPath();
            ctx.moveTo(shift1 + pos.start1, y1 + height + gap);
            ctx.bezierCurveTo(shift1 + pos.start1, y1 + height + bezierOffset, shift2 + pos.start2, y2 - bezierOffset, shift2 + pos.start2, y2 - gap);
            ctx.lineTo(shift2 + pos.end2, y2 - gap);
            ctx.bezierCurveTo(shift2 + pos.end2, y2 - bezierOffset, shift1 + pos.end1, y1 + height + bezierOffset, shift1 + pos.end1, y1 + height + gap);
            ctx.closePath();
            ctx.stroke();
            ctx.fill();
            
            // Draw blue highlight border for highlighted item
            if (isHighlighted) {
                ctx.strokeStyle = '#1976d2';
                ctx.lineWidth = 1.5;
                
                // Draw border around bar 1 (bottom bar - text 1)
                ctx.strokeRect(shift1 + pos.start1, y1, width1, height);
                
                // Draw border around bar 2 (top bar - text 2)
                ctx.strokeRect(shift2 + pos.start2, y2, width2, height);
                
                // Draw border around the bezier connection
                ctx.stroke();
                
                ctx.lineWidth = 1;
            }
        });
        
        ctx.globalAlpha = 1.0;

        // Glossy fade: top 1/3 of each bar painted with white at decreasing opacity
        // (same formula as ReportOverviewCanvas: GD alpha 90-9*row, css = gdAlpha/127)
        const fadeRows = Math.floor(height / 3.0);
        for (let row = 0; row < fadeRows; row++) {
            const cssOpacity = (90 - 9 * row) / 127;
            ctx.fillStyle = `rgba(255,255,255,${cssOpacity.toFixed(3)})`;
            ctx.fillRect(shift1, y1 + row, length1 * scale, 1);
            ctx.fillRect(shift2, y2 + row, length2 * scale, 1);
        }

        // add frame around bars
        ctx.strokeStyle = borderColor;
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.rect(shift1, y1, length1 * scale, height);
        ctx.rect(shift2, y2, length2 * scale, height);
        ctx.stroke();

        // add descriptions
        ctx.shadowBlur = 0;
        ctx.fillStyle = textColor;
        ctx.font = '14px Roboto, Helvetica, Arial, sans-serif';
        var text1Overflow = Math.max(0, shift1 + textPadding + ctx.measureText(state.sourceMarkup.Text1.Name).width - width);
        var text2Overflow = Math.max(0, shift2 + textPadding + ctx.measureText(state.sourceMarkup.Text2.Name).width - width);
        ctx.fillText(state.sourceMarkup.Text1.Name, textPadding + shift1 - text1Overflow, y1 + 17);
        ctx.fillText(state.sourceMarkup.Text2.Name, textPadding + shift2 - text2Overflow, y2 + 17);

    }, [state.sourceMarkup, state.ui.colors, calculateLayout]);

    // Hit test to find which position the mouse is over.
    // Collects ALL matching candidates and returns the one whose visual centre
    // is closest to the mouse X — this prevents jumping when small segments are
    // packed together, because multiple bezier fills and bar rects can overlap.
    const hitTest = useCallback((mouseX, mouseY) => {
        const positions = positionsRef.current;
        const layout = layoutRef.current;
        if (!layout.y1) return null;

        const { y1, y2, height, shift1, shift2, gap, bezierOffset } = layout;
        const hitPadding = 3;

        // Sort keys by start1 so spatial order drives iteration (not PIdx order)
        const sortedKeys = Object.keys(positions).sort(
            (a, b) => positions[a].start1 - positions[b].start1
        );

        // --- Pass 1: exact bar hits — collect all, pick nearest centre ---
        let bestKey = null;
        let bestDist = Infinity;

        for (const key of sortedKeys) {
            const pos = positions[key];
            const w1 = Math.max(1.0, pos.end1 - pos.start1);
            const w2 = Math.max(1.0, pos.end2 - pos.start2);

            if (mouseY >= y1 && mouseY <= y1 + height &&
                mouseX >= shift1 + pos.start1 && mouseX <= shift1 + pos.start1 + w1) {
                const dist = Math.abs(mouseX - (shift1 + pos.start1 + w1 / 2));
                if (dist < bestDist) { bestDist = dist; bestKey = key; }
            }
            if (mouseY >= y2 && mouseY <= y2 + height &&
                mouseX >= shift2 + pos.start2 && mouseX <= shift2 + pos.start2 + w2) {
                const dist = Math.abs(mouseX - (shift2 + pos.start2 + w2 / 2));
                if (dist < bestDist) { bestDist = dist; bestKey = key; }
            }
        }
        if (bestKey !== null) return bestKey;

        // --- Pass 2: bezier link area — collect all path hits, pick nearest centre ---
        const linkTop = y1 + height + gap;
        const linkBottom = y2 - gap;
        if (mouseY >= linkTop && mouseY <= linkBottom) {
            const candidates = [];
            for (const key of sortedKeys) {
                const pos = positions[key];
                const w1 = Math.max(1.0, pos.end1 - pos.start1);
                const w2 = Math.max(1.0, pos.end2 - pos.start2);
                const hitLineWidth = Math.max(12, w1, w2);
                const linkLeft  = Math.min(shift1 + pos.start1, shift2 + pos.start2) - hitLineWidth / 2;
                const linkRight = Math.max(shift1 + pos.end1,   shift2 + pos.end2)   + hitLineWidth / 2;
                if (mouseX < linkLeft || mouseX > linkRight) continue;
                const canvas = canvasRef.current;
                if (!canvas) continue;
                const ctx = canvas.getContext('2d');
                // Reset transform so path coordinates match raw mouse coordinates
                ctx.save();
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.beginPath();
                ctx.moveTo(shift1 + pos.start1, y1 + height + gap);
                ctx.bezierCurveTo(shift1 + pos.start1, y1 + height + bezierOffset, shift2 + pos.start2, y2 - bezierOffset, shift2 + pos.start2, y2 - gap);
                ctx.lineTo(shift2 + pos.end2, y2 - gap);
                ctx.bezierCurveTo(shift2 + pos.end2, y2 - bezierOffset, shift1 + pos.end1, y1 + height + bezierOffset, shift1 + pos.end1, y1 + height + gap);
                ctx.closePath();
                // isPointInPath misses near-vertical (thin) beziers because fill area → 0.
                // isPointInStroke catches those cases with a generous hit width.
                const savedLineWidth = ctx.lineWidth;
                ctx.lineWidth = hitLineWidth;
                const inPath = ctx.isPointInPath(mouseX, mouseY) || ctx.isPointInStroke(mouseX, mouseY);
                ctx.lineWidth = savedLineWidth;
                ctx.restore();
                if (inPath) {
                    // Interpolate centre-x between bar1 and bar2 at the mouse Y
                    const t = (mouseY - linkTop) / Math.max(1, linkBottom - linkTop);
                    const cx = (shift1 + pos.start1 + w1 / 2) * (1 - t) + (shift2 + pos.start2 + w2 / 2) * t;
                    candidates.push({ key, dist: Math.abs(mouseX - cx) });
                }
            }
            if (candidates.length > 0) {
                candidates.sort((a, b) => a.dist - b.dist);
                return candidates[0].key;
            }
        }

        // --- Pass 3: padded bar fallback for very thin segments — nearest centre wins ---
        let bestPaddedKey = null;
        let bestPaddedDist = Infinity;
        for (const key of sortedKeys) {
            const pos = positions[key];
            const w1 = Math.max(1.0, pos.end1 - pos.start1);
            const w2 = Math.max(1.0, pos.end2 - pos.start2);
            if (mouseX >= shift1 + pos.start1 - hitPadding && mouseX <= shift1 + pos.start1 + w1 + hitPadding &&
                mouseY >= y1 - hitPadding && mouseY <= y1 + height + hitPadding) {
                const dist = Math.abs(mouseX - (shift1 + pos.start1 + w1 / 2));
                if (dist < bestPaddedDist) { bestPaddedDist = dist; bestPaddedKey = key; }
            }
            if (mouseX >= shift2 + pos.start2 - hitPadding && mouseX <= shift2 + pos.start2 + w2 + hitPadding &&
                mouseY >= y2 - hitPadding && mouseY <= y2 + height + hitPadding) {
                const dist = Math.abs(mouseX - (shift2 + pos.start2 + w2 / 2));
                if (dist < bestPaddedDist) { bestPaddedDist = dist; bestPaddedKey = key; }
            }
        }
        if (bestPaddedKey !== null) return bestPaddedKey;

        return null;
    }, []);

    // Initial draw
    useEffect(() => {
        draw(hoveredPIdx);
    }, [state.sourceMarkup, hoveredPIdx, draw]);

    // Mouse event handlers
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            const hitKey = hitTest(mouseX, mouseY);
            
            if (onHover) {
                onHover(hitKey);
            }
            
            canvas.style.cursor = hitKey ? 'pointer' : 'default';
        };

        const handleMouseLeave = () => {
            if (onHover) {
                onHover(null);
            }
            canvas.style.cursor = 'default';
        };

        const handleClick = (e) => {
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            const hitKey = hitTest(mouseX, mouseY);
            
            if (hitKey && onClick) {
                onClick(hitKey);
            }
        };

        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseleave', handleMouseLeave);
        canvas.addEventListener('click', handleClick);

        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseleave', handleMouseLeave);
            canvas.removeEventListener('click', handleClick);
        };
    }, [hitTest, onHover, onClick]);

    return <canvas ref={canvasRef} {...canvasProps} />;
}


